/**
 * useCalendarActivities - Hook especializado para buscar atividades do calendário
 * Usa a RPC get_user_activities_feed para consistência com a Timeline
 * 
 * @version 1.3.0
 * RELEASE: 2026-05-15 v1.3.0 - p_limit=1000 evita truncamento do filtro "Todos"
 *                              (default 30 da RPC cortava dias mais antigos no calendário)
 * RELEASE: 2026-02-20 v1.2.0 - Adicionado cancelActivity mutation + bookingCancelToken
 * RELEASE: 2026-02-09 v1.1.0 - Refatorado para usar RPC unificada get_user_activities_feed
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { format } from 'date-fns';
import { createLogger } from '@/lib/logger';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';

const logger = createLogger('CalendarActivities');

import type { EventInput } from '@fullcalendar/core';

// Tipo para atividade retornada da RPC (mapeado para formato interno)
interface CalendarActivity {
  id: string;
  title: string;
  description: string | null;
  type: 'note' | 'call' | 'meeting' | 'task' | 'email';
  scheduled_at: string;
  completed_at: string | null;
  performed_by_user_id: string | null;
  deal_id: string | null;
  deal_title?: string;
  customer_name?: string;
  assigned_user_name?: string;
   conferencing_link?: string | null;
  /** Token para cancelamento do booking vinculado */
  booking_cancel_token?: string | null;
  /** Status do booking vinculado */
  booking_status?: string | null;
  /** Nome do responsável pelo agendamento (host) */
  host_user_name?: string | null;
  /** Avatar do responsável (v1.1.0 da RPC) */
  host_avatar_url?: string | null;
  /** Email do calendário vinculado ao host no tenant */
  calendar_email?: string | null;
  /** Cor do calendário/event_type (v1.1.0 da RPC) */
  calendar_color?: string | null;
}

// Mapeamento de cores por tipo de atividade
export const ACTIVITY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  call: { bg: 'hsl(142, 71%, 45%)', border: 'hsl(142, 71%, 35%)', text: 'white' },
  meeting: { bg: 'hsl(25, 95%, 53%)', border: 'hsl(25, 95%, 43%)', text: 'white' },
  email: { bg: 'hsl(270, 91%, 65%)', border: 'hsl(270, 91%, 55%)', text: 'white' },
  task: { bg: 'hsl(48, 96%, 53%)', border: 'hsl(48, 96%, 43%)', text: 'black' },
  note: { bg: 'hsl(217, 91%, 60%)', border: 'hsl(217, 91%, 50%)', text: 'white' },
};

// Labels por tipo de atividade
export const ACTIVITY_LABELS: Record<string, string> = {
  call: 'Ligação',
  meeting: 'Reunião',
  email: 'E-mail',
  task: 'Lembrete',
  note: 'Nota',
};

// Ícones por tipo de atividade
export const ACTIVITY_ICONS: Record<string, string> = {
  call: '📞',
  meeting: '🤝',
  email: '📧',
  task: '✓',
  note: '📝',
};

/**
 * Transforma atividade do banco para formato EventInput do FullCalendar
 */
function transformToCalendarEvent(activity: CalendarActivity): EventInput {
  const colors = ACTIVITY_COLORS[activity.type] || ACTIVITY_COLORS.note;
  const isCompleted = !!activity.completed_at;
  
  // Calcula duração padrão de 30 minutos para visualização
  const startDate = new Date(activity.scheduled_at);
  const endDate = new Date(startDate.getTime() + 30 * 60 * 1000); // +30 min
  
  return {
    id: activity.id,
    title: `${ACTIVITY_ICONS[activity.type]} ${activity.title}`,
    start: activity.scheduled_at,
    end: endDate.toISOString(),
    backgroundColor: colors.bg, // Cor sólida; diferenciação visual via CSS (.fc-event-completed)
    borderColor: colors.border,
    textColor: colors.text,
    classNames: isCompleted ? ['fc-event-completed'] : [],
    extendedProps: {
      activity,
      type: activity.type,
      isCompleted,
      dealId: activity.deal_id,
      dealTitle: activity.deal_title,
      customerName: activity.customer_name,
      description: activity.description,
      conferencingLink: activity.conferencing_link || null,
      bookingCancelToken: activity.booking_cancel_token || null,
      bookingStatus: activity.booking_status || null,
      hostUserName: activity.host_user_name || null,
      hostAvatarUrl: activity.host_avatar_url || null,
      calendarEmail: activity.calendar_email || null,
      calendarColor: activity.calendar_color || null,
    },
  };
}

interface UseCalendarActivitiesParams {
  startDate: Date;
  endDate: Date;
  typeFilter?: string | null;
  /** Filtro por workspace do negócio */
  workspaceId?: string | null;
  /** Filtro por vendedor do negócio */
  sellerId?: string | null;
  /** Filtro por responsável pela atividade */
  performedByUserId?: string | null;
  /** Filtro por status: all, open, completed */
  status?: string | null;
  /** Filtro por tipo de evento (event_type_id) - v1.7.0 */
  eventTypeId?: string | null;
}

interface UseCalendarActivitiesResult {
  events: EventInput[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  completeActivity: ReturnType<typeof useMutation>;
  reopenActivity: ReturnType<typeof useMutation>;
  updateActivityDate: ReturnType<typeof useMutation>;
  cancelActivity: ReturnType<typeof useMutation>;
}

/**
 * Hook para buscar e gerenciar atividades do calendário
 */
export function useCalendarActivities({
  startDate,
  endDate,
  typeFilter,
  workspaceId,
  sellerId,
  performedByUserId,
  status,
  eventTypeId,
}: UseCalendarActivitiesParams): UseCalendarActivitiesResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Query para buscar atividades usando a RPC unificada
  const { data: activities = [], isLoading, isError, refetch } = useQuery({
    queryKey: [
      'calendar-activities',
      currentTenant?.id,
      startDate.toISOString(),
      endDate.toISOString(),
      typeFilter,
      workspaceId,
      sellerId,
      performedByUserId,
      status,
      eventTypeId,
    ],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      // v1.5.0: Filtro server-side - 'online' envia meeting + p_filter_online_only
      const isOnline = typeFilter === 'online';
      const rpcActivityType = isOnline ? 'meeting' : (typeFilter || null);

      // v1.7.3: Passa timezone local para filtros de data corretos
      const { data, error } = await supabase.rpc('get_user_activities_feed', {
        p_start_date: format(startDate, 'yyyy-MM-dd'),
        p_end_date: format(endDate, 'yyyy-MM-dd'),
        p_activity_type: rpcActivityType,
        p_tenant_hint: currentTenant.id,
        p_filter_by_scheduled: true,
        p_workspace_id: workspaceId || null,
        p_seller_id: sellerId || null,
        p_user_id: performedByUserId || null,
        p_status: status && status !== 'all' ? status : null,
        p_filter_online_only: isOnline, // v1.5.0: filtro server-side
        p_event_type_id: eventTypeId || null, // v1.7.0: filtro por tipo de evento
        p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // v1.7.3: timezone local
        p_limit: 1000, // v1.3.0: calendário precisa do mês inteiro; default 30 da RPC cortava dias
      } as Record<string, unknown>);

      if (error) {
        throw error;
      }

      // Filtra apenas atividades com agendamento (calendário só mostra agendadas)
      const scheduledActivities = (data || []).filter(
        (row: any) => row.scheduled_at
      );

      // Transforma dados da RPC para formato interno do calendário
      const transformed: CalendarActivity[] = scheduledActivities.map((item: any) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        type: item.activity_type, // RPC retorna 'activity_type', não 'type'
        scheduled_at: item.scheduled_at,
        completed_at: item.completed_at,
        performed_by_user_id: item.performed_by_id, // RPC retorna 'performed_by_id'
        deal_id: item.deal_id,
        deal_title: item.deal_title,
        customer_name: item.customer_nome, // RPC retorna 'customer_nome'
      }));

      // v2.0.0: Dados de booking via RPC get_booking_data_for_activities (eliminadas queries diretas a scheduling_bookings)
      const activityIds = transformed.map((a) => a.id);
      if (activityIds.length > 0) {
        const { data: bookingsData } = await supabase.rpc('get_booking_data_for_activities', {
          p_activity_ids: activityIds,
          p_tenant_hint: currentTenant.id,
        });

        if (bookingsData && (bookingsData as any[]).length > 0) {
          // Enriquece atividades com dados retornados pela RPC (já inclui host_name e calendar_email)
          for (const act of transformed) {
            const bk = (bookingsData as any[]).find((b: any) => b.crm_activity_id === act.id);
            if (bk) {
              act.conferencing_link = bk.conferencing_link || null;
              act.booking_cancel_token = bk.cancel_token || null;
              act.booking_status = bk.status || null;
              act.host_user_name = bk.host_user_name || null;
              act.host_avatar_url = bk.host_avatar_url || null;
              act.calendar_email = bk.calendar_email || null;
              act.calendar_color = bk.calendar_color || null;
            }
          }
        }
      }

      return transformed;
    },
    enabled: !!currentTenant?.id,
    staleTime: 60 * 1000, // Cache de 1 minuto
  });

  // Transforma atividades em eventos do FullCalendar
  const events: EventInput[] = activities.map(transformToCalendarEvent);

  // Mutation para concluir atividade
  const completeActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase
        .from('crm_deal_activities')
        .update({ completed_at: new Date().toISOString() })
        .eq('id', activityId);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalida todos os caches relacionados para sincronização global
      queryClient.invalidateQueries({ queryKey: ['calendar-activities'] });
      queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-list-activities'] });
      queryClient.invalidateQueries({ queryKey: ['standalone-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-kpi'] });
    },
  });

  // Mutation para reabrir atividade (com verificação de conflito de horário)
  const reopenActivity = useMutation({
    mutationFn: async (activityId: string) => {
      // v2.0.0: Toda lógica de reabertura agora é atômica via RPC (eliminados SELECTs/UPDATEs diretos a scheduling_bookings)
      const { error } = await supabase.rpc('reopen_scheduling_booking', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        // Mapeia erro de conflito para manter compatibilidade com handler existente
        if (error.message?.includes('SLOT_CONFLICT')) {
          throw new Error('SLOT_CONFLICT');
        }
        throw error;
      }
    },
    onSuccess: () => {
      // Invalida todos os caches relacionados para sincronização global
      queryClient.invalidateQueries({ queryKey: ['calendar-activities'] });
      queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-list-activities'] });
      queryClient.invalidateQueries({ queryKey: ['standalone-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-kpi'] });
    },
  });

  // Mutation para atualizar data da atividade (drag & drop)
  const updateActivityDate = useMutation({
    mutationFn: async ({ activityId, newDate }: { activityId: string; newDate: Date }) => {
      const { error } = await supabase
        .from('crm_deal_activities')
        .update({ scheduled_at: newDate.toISOString() })
        .eq('id', activityId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-activities'] });
      queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-list-activities'] });
      queryClient.invalidateQueries({ queryKey: ['standalone-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-kpi'] });
    },
  });

  // Mutation para cancelar agendamento online (v1.2.0)
  const cancelActivity = useMutation({
    mutationFn: async ({ cancelToken, reason }: { cancelToken: string; reason?: string }) => {
      logger.info('Cancelando booking pelo calendário admin');

      // Chama a mesma RPC pública de cancelamento
      const { data, error } = await supabase.rpc('cancel_scheduling_booking', {
        p_cancel_token: cancelToken,
        p_reason: reason || null,
      });

      if (error) {
        logger.error('Erro ao cancelar booking', error);
        throw error;
      }

      const result = data as unknown as { google_event_id: string | null; user_id: string };

      // Fire-and-forget: deletar evento do Google Calendar (mesmo padrão do useCancelBooking)
      if (result.google_event_id && result.user_id) {
        try {
          // Obter token de autenticação para a Edge Function calendar-provider
          const { data: { session } } = await supabase.auth.getSession();
          const authToken = session?.access_token;

          const calendarUrl = getEdgeFunctionUrl('calendar-provider');
          fetch(calendarUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
            },
            body: JSON.stringify({
              action: 'delete-event',
              user_id: result.user_id,
              google_event_id: result.google_event_id,
            }),
          }).catch((err) => {
            logger.warn('Erro ao deletar evento do Calendar (fire-and-forget)', err);
          });
        } catch (calErr) {
          logger.warn('Erro ao disparar delete-event no Calendar', calErr);
        }
      }

      return result;
    },
    onSuccess: () => {
      // Invalida todos os caches relacionados para sincronização global
      queryClient.invalidateQueries({ queryKey: ['calendar-activities'] });
      queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-list-activities'] });
      queryClient.invalidateQueries({ queryKey: ['standalone-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-kpi'] });
    },
  });

  return {
    events,
    isLoading,
    isError,
    refetch,
    completeActivity,
    reopenActivity,
    updateActivityDate,
    cancelActivity,
  };
}
